import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StepperOrientation } from '@angular/material/stepper';

import { PatSympComponent } from '../../pat-symp/pat-symp.component';
import { AddVitalsComponent } from '../../patient-observation/add-vitals/add-vitals.component';
import { AddObservationComponent } from '../../patient-observation/add-observation/add-observation.component';
import { FinalizeTriageDialogComponent } from '../finalize-triage-dialog/finalize-triage-dialog.component';

@Component({
  selector: 'app-triage-stepper-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatTabsModule,
    MatCardModule,
    MatCheckboxModule,
    FormsModule,
    PatSympComponent,
    AddVitalsComponent,
    AddObservationComponent,
    FinalizeTriageDialogComponent
  ],
  template: `
    <h2 mat-dialog-title>Triage Assessment</h2>
    <mat-dialog-content class="mat-typography" style="padding: 0; max-height: 85vh;">
      <mat-stepper [linear]="true" #stepper class="triage-stepper" [orientation]="(stepperOrientation$ | async)!">
        
        <!-- Step 1: Chief Complaint & Symptoms -->
        <mat-step [optional]="true">
          <ng-template matStepLabel>Chief Complaint & Symptoms</ng-template>
          <div class="step-content-wrapper">
            <app-pat-symp #sympChild [isStepperMode]="true" (saveCompleted)="stepper.next()"></app-pat-symp>
          </div>
          <div class="stepper-actions">
            <button mat-flat-button color="primary" 
                    (click)="(sympChild.observationForEachArrayElement?.length || 0) > 0 ? sympChild.addSystemGeneratedValuesAndSubmit() : stepper.next()">
              {{ (sympChild.observationForEachArrayElement?.length || 0) > 0 ? 'Save & Next' : 'Next' }} <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </mat-step>

        <!-- Step 2: Vitals -->
        <mat-step [optional]="true">
          <ng-template matStepLabel>Vitals</ng-template>
          <div class="step-content-wrapper">
            <app-add-vitals #vitalsChild [isStepperMode]="true" (saveCompleted)="stepper.next()"></app-add-vitals>
          </div>
          <div class="stepper-actions">
            <button mat-button matStepperPrevious>Back</button>
            <button mat-flat-button color="primary" 
                    [disabled]="vitalsChild.vitalsFormGroup?.invalid && vitalsChild.vitalsFormGroup?.dirty"
                    (click)="vitalsChild.isSubmitDisabled ? stepper.next() : vitalsChild.submitVitals()">
              {{ vitalsChild.isSubmitDisabled ? 'Next' : 'Save & Next' }} <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </mat-step>

        <!-- Step 3: Physical Exam -->
        <mat-step [optional]="true">
          <ng-template matStepLabel>Physical Exam</ng-template>
          <div class="step-content-wrapper">
            <app-add-observation #obsChild [isStepperMode]="true" [isAnyCategory]="false" observationCategoryValue="exam" (saveCompleted)="stepper.next()"></app-add-observation>
          </div>
          <div class="stepper-actions">
            <button mat-button matStepperPrevious>Back</button>
            <button mat-flat-button color="primary" 
                    [disabled]="obsChild.physicalExamForm?.invalid && obsChild.physicalExamForm?.dirty"
                    (click)="(obsChild.examArray?.length || 0) > 0 ? obsChild.onSubmitPhysicalExamForm() : stepper.next()">
              {{ (obsChild.examArray?.length || 0) > 0 ? 'Save & Next' : 'Next' }} <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </mat-step>

        <!-- Step 4: High-Risk Indicators -->
        <mat-step [optional]="true">
          <ng-template matStepLabel>High-Risk Indicators</ng-template>
          <div class="step-content-wrapper">
            <mat-card appearance="outlined">
              <mat-card-header>
                <mat-card-title class="warn-text">High-Risk Physical Observations</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="checklist" style="display: flex; flex-direction: column; gap: 12px; margin-top: 10px;">
                  <mat-checkbox *ngFor="let indicator of data.highRiskIndicators" [(ngModel)]="indicator.checked" color="warn">
                    {{ indicator.label }}
                  </mat-checkbox>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
          <div class="stepper-actions">
            <button mat-button matStepperPrevious>Back</button>
            <!-- Call saveHighRisk() which returns a Promise, and then move to next step -->
            <button mat-flat-button color="primary" (click)="saveAndNextHighRisk(stepper)">Save & Next <mat-icon>arrow_forward</mat-icon></button>
          </div>
        </mat-step>

        <!-- Step 4: Finalize Priority -->
        <mat-step [completed]="false">
          <ng-template matStepLabel>Triage Priority</ng-template>
          <div class="step-content-wrapper">
            <app-finalize-triage-dialog [isStepperMode]="true" (onComplete)="onComplete($event)" (onCancel)="stepper.previous()"></app-finalize-triage-dialog>
          </div>
        </mat-step>

      </mat-stepper>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .step-content-wrapper {
      padding: 16px;
      min-height: 400px;
    }
    .stepper-actions {
      margin-top: 24px;
      display: flex;
      gap: 12px;
      align-items: center;
      padding: 16px;
      border-top: 1px solid var(--mat-sys-outline-variant);
    }
    .warn-text {
      color: var(--mat-sys-error) !important;
    }
  `]
})
export class TriageStepperDialogComponent implements OnInit {
  dialogRef = inject(MatDialogRef<TriageStepperDialogComponent>);
  data = inject(MAT_DIALOG_DATA);
  breakpointObserver = inject(BreakpointObserver);

  stepperOrientation$: Observable<StepperOrientation>;

  constructor() {
    // Breakpoints.Small and XSmall covers iPads and phones, making stepper vertical
    this.stepperOrientation$ = this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, '(max-width: 960px)'])
      .pipe(map(result => result.matches ? 'vertical' : 'horizontal'));
  }

  ngOnInit() {}

  async saveAndNextHighRisk(stepper: any) {
    // Notify parent to save high risk indicators. Await the promise before moving to next step
    if (this.data.onSaveHighRisk) {
      await this.data.onSaveHighRisk();
    }
    stepper.next();
  }

  onComplete(result: any) {
    this.dialogRef.close({ action: 'complete', result });
  }
}
